import { AppConstants } from "@unpispas/upp-defs";
import { adhocService } from "@unpispas/upp-base";
import { clockService } from "@unpispas/upp-base";
import { languageService } from "@unpispas/upp-base";

import { dataService } from "../../data";

import { Place } from "../objects/place";
import { Product } from "../objects/product";
import { Category } from "../objects/category";
import { CategoryDep } from "../objects/categorydep";
import { ProductOpt } from "../objects/productopt";
import { Preselect } from "../objects/preselect";
import { PreselectOpt } from "../objects/preselectopt";
import { I18nValue, I18nValues } from "../objects/i18nvalue";

export class Catalog {
    private _doCatalogUrl = 'catalog/catalog';

    constructor(private adhoc: adhocService, private clock: clockService, private data: dataService){
        // nothing to do
    }

    /*********************************/
    /* LOADING METHODS               */
    /*********************************/

    private _toCtgref(place: Place, ctgref: string): string {
        return place.objid + "_" + ctgref;
    }

    private _getData(data: any, field: string, deflt: any | null = null): any {
        return (field in data) ? data[field] : deflt;
    }

    private _getPhoto(data: any, field: string, deflt: string | null = null): string | null {
        return (field in data) ? AppConstants.baseURL + 'resources/catalog/images/' + data[field] : deflt;
    }

    private _lang(): languageService {
        return this.data.injector.get(languageService);
    }

    private _catalogI18n(raw: unknown): I18nValue | null {
        if (raw === null || raw === undefined || typeof raw !== 'object' || Array.isArray(raw)) {
            return null;
        }

        const _map: I18nValues = {};
        for (const _lang of this._lang().GetLanguages()) {
            const _value = (raw as Record<string, unknown>)[_lang];
            if (typeof _value === 'string') {
                const _text = _value.trim();
                if (_text) {
                    _map[_lang] = _text;
                }
            }
        }

        if (Object.keys(_map).length === 0) {
            return null;
        }

        const _row = new I18nValue(null, this.data, { volatile: true });
        _row.values = _map;
        return _row;
    }

    private _assignI18n(
        target: { name?: I18nValue | null; description?: I18nValue | null },
        field: 'name' | 'description',
        raw: unknown,
    ): void {
        const _row = this._catalogI18n(raw);
        if (_row) {
            target[field] = _row;
        }
    }

    private _findOption(product: Product, data: string): ProductOpt | null{
        if (data){
            for(const _category of product.categories){
                for(const _option of _category.options){
                    if (_option.ctgref == data){
                        return _option;
                    }
                }
            }

            console.error("[CATALOG] option '" + data + "' not found (problem in catalog?)", product);
        }

        return null;
    }

    private _findProduct(place: Place, data: string): Product | null {
        if (data){
            const _data = this._toCtgref(place, data);
            let _catalog: Product | null = null;

            for(const _product of place.products){
                if ((_product.IsValid) && (_product.ctgref == _data)){
                    if (!_product.IsCatalog){
                        return _product;
                    }

                    _catalog = _product;
                }
            }

            if (_catalog){
                return _catalog;
            }

            console.error("[CATALOG] product '" + data + "' not found (problem in catalog?)", place);    
        }

        return null;
    }

    private _readOption(category: Category, data: any): ProductOpt{
        const _ddbb: Record<string, any> = {
            sort: category.options.length,
            status: 'CT',
            active: false,
            catalog: true,
            product: null,
            category: null,
            price: null,

            ctgref: this._getData(data, 'ref', null), 
            name: null,
            description: null,
            flags: null
        };

        const _option = new ProductOpt(null, this.data);
        if (_option){
            _option.Info = _ddbb;
            this._assignI18n(_option, 'name', this._getData(data, 'name', null));
            this._assignI18n(_option, 'description', this._getData(data, 'description', null));
            _option.product = category.product;
            _option.category = category;
            _option.IsLoaded = true;
            _option.IsCatalog = true;
        }

        return _option;
    }

    private _readDepend(product: Product, category: Category, data: string): CategoryDep {
        const _depend = new CategoryDep(null, this.data);
        if (_depend){
            _depend.category = category;
            _depend.option = this._findOption(product, data);
            _depend.status = 'CT';

            _depend.IsLoaded = true;
            _depend.IsCatalog = true;
        }

        return _depend;
    }

    private _readCategory(product: Product, data: any): Category{
        const _ddbb: Record<string, any> = {
            sort: product.categories.length,
            status: 'CT',
            product: null,

            name: null,
            type: this._getData(data, 'type', null),
            fxmin: this._getData(data, 'fixed', [0, 0])[0],
            fxmax: this._getData(data, 'fixed', [0, 0])[1]
        };

        const _category = new Category(null, this.data);
        if (_category){
            _category.Info =_ddbb;
            this._assignI18n(_category, 'name', this._getData(data, 'name', null));
            _category.product = product;

            // add 'options' children
            const _options = this._getData(data, 'options', []);
            for (const _data of _options){
                const _option: ProductOpt = this._readOption(_category, _data);
                if (_option){
                    _category.AddOption(_option);
                }
            }

            // add 'depends' children
            const _depends = this._getData(data, 'depends', []);
            for (const _data of _depends){
                const _depend: CategoryDep = this._readDepend(product, _category, _data);
                if (_depend){
                    _category.AddDepend(_depend);
                }
            }

            _category.IsLoaded = true;
            _category.IsCatalog = true;
        }

        return _category;
    }

    private _readSelect(product: Product, data: any): Preselect{
        const _ddbb: Record<string, any> = {
            sort: product.selects.length,
            status: 'CT',
            product: null,
            name: null,
            code: null,
            barcode: null,
            alias: null,
            photo: {
                url: this._getPhoto(data, 'photo', null),
                b64: null
            },
            description: null,
        };

        const _select = new Preselect(null, this.data);
        if (_select){
            _select.Info = _ddbb;
            this._assignI18n(_select, 'name', this._getData(data, 'name', null));
            this._assignI18n(_select, 'description', this._getData(data, 'description', null));
            _select.product = product;

            // add 'options' children
            const _options = this._getData(data, 'options', []);
            for (const _data of _options){
                const _option: PreselectOpt = new PreselectOpt(null, this.data);
                if (_option){
                    _option.select = _select;
                    _option.option = this._findOption(product, _data);
                    _option.status = 'AC';
                    _select.AddOption(_option);
                }
            }

            _select.IsLoaded = true;
            _select.IsCatalog = true;
        }

        return _select;
    }

    private _readProduct(place: Place, data: any): Product{
        const _ddbb: Record<string, any> = {
            sort: place.products.length,
            status: 'CT',
            place: null,
            price: null,
            taxrate: null,

            ctgref: this._toCtgref(place, this._getData(data, 'ref', null)),
            name: null,
            code: null,
            barcode: null,
            alias: null,
            photo: {
                url: this._getPhoto(data, 'photo', null),
                b64: null
            },
            description: null,
            isgroup: this._getData(data, 'isgroup', false),
            isfamily: this._getData(data, 'isfamily', false),
            generic: false,
            type: this._getData(data, 'type', null),
            parent: this._getData(data, 'parent', null),
            flags: null,
            next: null
        };

        const _product = new Product(null, this.data);
        if (_product){
            _product.Info = _ddbb;
            this._assignI18n(_product, 'name', this._getData(data, 'name', null));
            this._assignI18n(_product, 'description', this._getData(data, 'description', null));
            _product.parent = this._findProduct(place, this._getData(data, 'parent', null));

            // add 'categories' children
            const _categories = this._getData(data, 'categories', []);
            for (const _data of _categories){
                const _category: Category = this._readCategory(_product, _data);
                if (_category){
                    _product.AddCategory(_category);
                }
            }

            // add 'selects' children
            const _preselects = this._getData(data, 'selects', []);
            for (const _data of _preselects){
                const _select : Preselect = this._readSelect(_product, _data);
                if (_select){
                    _product.AddSelect(_select);
                }
            }

            _product.IsLoaded = true;
            _product.place = place;
            _product.IsCatalog = true;
        }

        return _product;
    }

    private _addProduct(place: Place, product: Product) {
        let _candidate: Product | null = null;
        for (const _product of place.products){
            if (_product.IsValid && (_product.ctgref == product.ctgref)){
                if (!_product.IsCatalog){
                    _candidate = _product;
                    break;
                }

                if (!_candidate){
                    _candidate = _product;
                }
            }
        }

        product.next = _candidate;    
        place.AddProduct(product);
    }

    private async _loadCatalog(place: Place) {
        console.info("[CATALOG] Loading products catalog..");

        this._isloaded = true;
        if (place.objid == null){
            throw new Error("[CATALOG] Cannot load catalog if place has not been commited!");
        }

        try {
            const catalog: Array <any> = await this.adhoc.DoRequest(this._doCatalogUrl)
            this.clock.ClockEnable('catalog', false);
            for (const data of catalog){
                const _product: Product = this._readProduct(place, data);
                if (_product){
                    this._addProduct(place, _product);
                }
            }
            this.clock.ClockEnable('catalog', true);
            console.info("[CATALOG] Loaded succesfully."); 
        }
        catch(error: any) {
            console.error("[REQUEST] Error [" + error.status + "] in http request '" + this._doCatalogUrl + "'");
            this._isloaded = false;
        }

        return this._isloaded;
    }

    /****************************/
    /* CATALOG INITIALIZATION   */
    /****************************/

    private _isloaded = false;
    get IsLoaded(): boolean {
        return this._isloaded;
    }

    async DoLoad(place: Place): Promise <boolean>{
        if (!this.IsLoaded){
            return await this._loadCatalog(place);
        }

        return true;    // already loaded
    }
}